import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Agent {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  role: "agent" | "admin";
  assigned_services: {
    id: string;
    name: string;
  }[];
}

export function useAgents() {
  return useQuery({
    queryKey: ["agents"],
    queryFn: async () => {
      // Get all agents and admins from user_roles
      const { data: rolesData, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id, role")
        .in("role", ["agent", "admin"]);

      if (rolesError) throw rolesError;
      if (!rolesData || rolesData.length === 0) return [];

      const userIds = rolesData.map((r) => r.user_id);

      // Get profiles for these users
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, email, first_name, last_name, display_name")
        .in("id", userIds);

      if (profilesError) throw profilesError;

      // Get services to see assignments
      const { data: servicesData, error: servicesError } = await supabase
        .from("services")
        .select("id, name, assigned_agent_id")
        .not("assigned_agent_id", "is", null);

      if (servicesError) throw servicesError;

      // Build agent list with their assigned services
      const agents: Agent[] = rolesData.map((roleRecord) => {
        const profile = profilesData?.find((p) => p.id === roleRecord.user_id);
        const assignedServices = (servicesData || [])
          .filter((s) => s.assigned_agent_id === roleRecord.user_id)
          .map((s) => ({ id: s.id, name: s.name }));

        return {
          id: roleRecord.user_id,
          email: profile?.email || null,
          first_name: profile?.first_name || null,
          last_name: profile?.last_name || null,
          display_name: profile?.display_name || null,
          role: roleRecord.role as "agent" | "admin",
          assigned_services: assignedServices,
        };
      });

      return agents;
    },
  });
}
